import { useMemo, useRef, useState } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DollarSignIcon,
  InfoIcon,
} from "lucide-react";

import { Column, Row, Text } from "@/components";
import { LeaderboardDetailSheet } from "@/components/leaderboard/LeaderboardDetailSheet";
import { LeaderboardLiquidJobs } from "@/components/leaderboard/LeaderboardLiquidJobs";
import { LeaderboardStafferBadges } from "@/components/leaderboard/LeaderboardStafferBadges";
import { MetricLeadersHoverCard } from "@/components/leaderboard/MetricLeadersHoverCard";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useInactiveUserIds } from "@/contexts/TaggableUsersContext";

import { REVENUE_GRADIENT } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  LeaderboardMetric,
  LeaderboardQueryParams,
  LeaderboardUser,
  SortOrder,
} from "@/types";

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type MetricColumn = {
  label: string;
  key: keyof LeaderboardUser;
  metricType: LeaderboardMetric | null;
  tooltip: string;
  format?: (value: number) => string;
};

interface ColumnSort {
  key: keyof LeaderboardUser;
  direction: SortOrder;
}

type SelectedMetricDetail = {
  metric: LeaderboardMetric;
  metricLabel: string;
  userId: number | null;
  userName: string;
} | null;

const metricColumns: MetricColumn[] = [
  {
    label: "Messages",
    key: "messages",
    metricType: null,
    tooltip: "Total text messages sent and received.",
  },
  {
    label: "Calls",
    key: "dials",
    metricType: LeaderboardMetric.DIALS,
    tooltip: "Total incoming and outgoing calls.",
  },
  {
    label: "Call Time",
    key: "totalCallDuration",
    metricType: LeaderboardMetric.DIALS,
    tooltip:
      "Total time spent on the phone across incoming and outgoing calls.",
    format: (seconds) => {
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      if (h > 0) return `${h.toLocaleString()}h ${m.toLocaleString()}m`;
      return `${m.toLocaleString()}m`;
    },
  },
  {
    label: "Screens",
    key: "screens",
    metricType: LeaderboardMetric.SCREENS,
    tooltip: "Candidates moved to the Screened status.",
  },
  {
    label: "Submittals",
    key: "submittals",
    metricType: LeaderboardMetric.SUBMITTALS,
    tooltip: "Candidates moved to the Batch Sent status.",
  },
  {
    label: "Interviews",
    key: "interviews",
    metricType: LeaderboardMetric.INTERVIEWS,
    tooltip: "Candidates moved to Interview status.",
  },
  {
    label: "Offers",
    key: "offers",
    metricType: LeaderboardMetric.OFFERS,
    tooltip: "Offers extended, by date offered.",
  },
  {
    label: "Placements",
    key: "placements",
    metricType: LeaderboardMetric.PLACEMENTS,
    tooltip: "Placements by date accepted.",
  },
  {
    label: "Revenue",
    key: "revenue",
    metricType: LeaderboardMetric.REVENUE,
    tooltip: "Total revenue earned from accepted placements.",
    format: (v) =>
      v.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }),
  },
];

const HeaderTooltip = ({ tooltip }: { tooltip: string }) => {
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <InfoIcon
          className="h-3 w-3 shrink-0 text-slate-400"
          onMouseEnter={() => {
            hoverTimerRef.current = setTimeout(() => {
              mixpanelUtils.trackHover(
                "Metric info tooltip",
                MIXPANEL_SCREENS.LEADERBOARD,
              );
            }, 300);
          }}
          onMouseLeave={() => {
            if (hoverTimerRef.current) {
              clearTimeout(hoverTimerRef.current);
              hoverTimerRef.current = null;
            }
          }}
        />
      </TooltipTrigger>
      <TooltipContent side="top">
        <p className="text-sm">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export const LeaderboardTable = ({
  queryParams,
  users,
}: {
  queryParams: LeaderboardQueryParams;
  users: LeaderboardUser[];
}) => {
  /* React Hooks */
  const [selectedMetricDetail, setSelectedMetricDetail] =
    useState<SelectedMetricDetail>(null);

  const [sort, setSort] = useState<ColumnSort>({
    key: "revenue",
    direction: SortOrder.Desc,
  });

  const inactiveUserIds = useInactiveUserIds();

  const handleSort = (key: keyof LeaderboardUser) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.LEADERBOARD,
      "Sort",
      key,
    );
    setSort((prev) =>
      prev.key === key
        ? {
            key,
            direction:
              prev.direction === SortOrder.Desc
                ? SortOrder.Asc
                : SortOrder.Desc,
          }
        : { key, direction: SortOrder.Desc },
    );
  };

  const stafferOptions = useMemo(
    () =>
      users.map((u) => ({
        label: `${u.firstName} ${u.lastName}`,
        value: u.userId,
      })),
    [users],
  );

  // Sort by the selected column and order.
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = a[sort.key] as number;
      const bVal = b[sort.key] as number;
      return sort.direction === SortOrder.Desc ? bVal - aVal : aVal - bVal;
    });
  }, [users, sort.key, sort.direction]);

  // Compute team totals.
  const totals = useMemo(() => {
    const t = {
      messages: 0,
      dials: 0,
      totalCallDuration: 0,
      screens: 0,
      submittals: 0,
      internalSubmittals: 0,
      interviews: 0,
      offers: 0,
      placements: 0,
      internalPlacements: 0,
      revenue: 0,
    };
    for (const user of users) {
      t.messages += user.messages;
      t.dials += user.dials;
      t.totalCallDuration += user.totalCallDuration;
      t.screens += user.screens;
      t.submittals += user.submittals;
      t.internalSubmittals += user.internalSubmittals;
      t.interviews += user.interviews;
      t.offers += user.offers;
      t.placements += user.placements;
      t.internalPlacements += user.internalPlacements;
      t.revenue += user.revenue;
    }
    return t;
  }, [users]);

  /* Render */
  const renderMetricValue = (
    key: keyof LeaderboardUser,
    displayValue: string | number,
    counts: { internalSubmittals: number; internalPlacements: number },
  ) => {
    if (key !== "submittals" && key !== "placements") return displayValue;

    const internalCount =
      key === "submittals"
        ? counts.internalSubmittals
        : counts.internalPlacements;
    if (internalCount === 0) return displayValue;

    return (
      <Row className="inline-flex items-baseline gap-1">
        {displayValue}
        <Text className="leaderboard-internal-count relative top-1 text-[11px] font-semibold leading-none">
          {internalCount.toLocaleString()}
        </Text>
      </Row>
    );
  };

  const renderSortIcon = (key: keyof LeaderboardUser) => {
    if (sort.key !== key) return null;
    if (sort.direction === SortOrder.Asc) {
      return <ArrowUpIcon className="h-3.5 w-3.5 text-slate-500" />;
    }
    return <ArrowDownIcon className="h-3.5 w-3.5 text-slate-500" />;
  };

  return (
    <Column>
      {/* Scrolls horizontally only; the table's full height grows into the
          page. No sticky header: overflow-x makes this the sticky container,
          and it never scrolls vertically. */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <TableHeader className="bg-white [&_th]:border-b [&_th]:border-slate-200">
            <TableRow className="border-none">
              <TableHead className="w-12">
                <Text variant="header">#</Text>
              </TableHead>
              <TableHead className="whitespace-nowrap">
                <Text variant="header">Staffer</Text>
              </TableHead>
              {metricColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className="cursor-pointer select-none whitespace-nowrap hover:bg-slate-100"
                  onClick={() => {
                    handleSort(col.key);
                  }}
                >
                  <Row className="items-center gap-1">
                    <HeaderTooltip tooltip={col.tooltip} />
                    <Text variant="header">{col.label}</Text>
                    {renderSortIcon(col.key)}
                  </Row>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((user, index) => (
              <TableRow key={user.userId}>
                <TableCell className="w-12">{index + 1}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Row className="items-center gap-2">
                    <Row className="relative h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200">
                      {user.isPreviousMonthRevenueWinner && (
                        <MetricLeadersHoverCard
                          title="Last month's top earners"
                          icon={DollarSignIcon}
                          gradient={REVENUE_GRADIENT}
                          firstMarker={
                            <Text className="text-lg leading-none">👑</Text>
                          }
                          leaders={leaderboardMetrics.topN(
                            sortedUsers,
                            "previousMonthRevenue",
                            3,
                          )}
                          valueOf={(u) => u.previousMonthRevenue}
                          format={formatting.currency}
                        >
                          <Row className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 cursor-default">
                            <Text className="text-sm leading-none">👑</Text>
                          </Row>
                        </MetricLeadersHoverCard>
                      )}
                      <p className="text-[10px] font-medium text-slate-900">
                        {user.firstName[0]}
                        {user.lastName[0]}
                      </p>
                    </Row>
                    {user.firstName}
                    {inactiveUserIds.has(user.userId) && (
                      <Text className="text-xs font-normal text-slate-400">
                        (inactive)
                      </Text>
                    )}
                    <LeaderboardLiquidJobs liquidJobs={user.liquidJobs} />
                    <LeaderboardStafferBadges user={user} users={sortedUsers} />
                  </Row>
                </TableCell>
                {metricColumns.map((col) => {
                  const value = user[col.key] as number;
                  const displayValue = col.format ? col.format(value) : value;

                  return (
                    <TableCell
                      key={col.key}
                      className="whitespace-nowrap"
                      onClick={
                        col.metricType
                          ? () => {
                              const metric = col.metricType;
                              if (!metric) return;

                              mixpanelUtils.trackButtonClick(
                                `Selected Metric: ${col.label}`,
                                MIXPANEL_SCREENS.LEADERBOARD,
                              );
                              setSelectedMetricDetail({
                                userId: user.userId,
                                userName: user.firstName,
                                metric,
                                metricLabel: col.label,
                              });
                            }
                          : undefined
                      }
                    >
                      {renderMetricValue(col.key, displayValue, user)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="w-12" />
              <TableCell className="whitespace-nowrap">Team Total</TableCell>
              {metricColumns.map((col) => {
                const value = totals[col.key as keyof typeof totals];
                const displayValue = col.format ? col.format(value) : value;
                return (
                  <TableCell
                    key={col.key}
                    className={cn(
                      "whitespace-nowrap",
                      col.metricType && "cursor-pointer",
                    )}
                    onClick={
                      col.metricType
                        ? () => {
                            const metric = col.metricType;
                            if (!metric) return;

                            mixpanelUtils.trackButtonClick(
                              `Selected Team Total: ${col.label}`,
                              MIXPANEL_SCREENS.LEADERBOARD,
                            );
                            setSelectedMetricDetail({
                              userId: null,
                              userName: "Team Total",
                              metric,
                              metricLabel: col.label,
                            });
                          }
                        : undefined
                    }
                  >
                    {renderMetricValue(col.key, displayValue, totals)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        </table>
      </div>
      <LeaderboardDetailSheet
        open={selectedMetricDetail !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedMetricDetail(null);
          }
        }}
        userId={selectedMetricDetail?.userId ?? null}
        userName={selectedMetricDetail?.userName ?? ""}
        metric={selectedMetricDetail?.metric ?? null}
        metricLabel={selectedMetricDetail?.metricLabel ?? ""}
        timePeriod={queryParams.timePeriod}
        dateRange={queryParams.dateRange}
        stafferOptions={stafferOptions}
      />
    </Column>
  );
};
